import { useEffect, useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

/**
 * Хук синхронизирует значение с query-параметром в URL.
 *
 * @param initValue - Начальное значение, если query нет
 * @param queryKey - Название параметра в query строке
 * @param convertCallback - Преобразует query строку в нужный тип
 */
function useRouterQueryTabs<T>(
  initValue: T,
  queryKey: string,
  convertCallback: (v: string | string[]) => T,
): [T, (value: T) => void] {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<T>(initValue);

  useEffect(() => {
    const query: any = queryString.parse(location.search);
    if (query[queryKey]) {
      setState(convertCallback(query[queryKey]!));
    } else {
      setState(initValue);
    }
  }, [location.search, queryKey, convertCallback, initValue]);

  const setValue = useCallback(
    (newValue: T) => {
      const query = queryString.parse(location.search);

      // 🔥 Важно: сохраняем ВСЕ текущие query, заменяя только нужный
      if (newValue === undefined || newValue === null || newValue === '') {
        delete query[queryKey];
      } else {
        query[queryKey] = String(newValue);
      }

      const newSearch = queryString.stringify(query);
      navigate(`${location.pathname}?${newSearch}`, {
        replace: true, // ⬅️ чтобы не добавлять в историю
      });

      setState(newValue); // ✅ обязательно обновляем state
    },
    [navigate, location.pathname, location.search, queryKey],
  );

  return [state, setValue];
}

export default useRouterQueryTabs;
